type NavItem = {
  id: string;
  label: string;
  icon: string;
  badge?: number;
};

type NavGroup = {
  title: string;
  items: NavItem[];
};

interface Props {
  activeSection: string;
  onNavigate: (section: string) => void;
  pendingCount: number;
  abnormalOrderCount?: number;
  onLogout: () => void;
}

const navGroups: NavGroup[] = [
  {
    title: "概览",
    items: [{ id: "dashboard", label: "平台总览", icon: "ri-global-line" }],
  },
  {
    title: "租户管理",
    items: [
      { id: "tenant-list", label: "租户列表", icon: "ri-building-4-line" },
      { id: "tenant-onboard", label: "开通租户", icon: "ri-add-box-line" },
    ],
  },
  {
    title: "套餐与计费",
    items: [
      { id: "plan-manager", label: "套餐管理", icon: "ri-price-tag-3-line" },
      { id: "billing", label: "收费记录", icon: "ri-bill-line" },
    ],
  },
  {
    title: "平台运营",
    items: [
      { id: "storage-analysis", label: "存储空间分析", icon: "ri-database-2-line" },
      { id: "image-library", label: "图片资源库", icon: "ri-image-2-line" },
      { id: "announcements", label: "平台公告", icon: "ri-megaphone-line" },
      { id: "registered-users", label: "注册用户", icon: "ri-user-follow-line" },
      { id: "fan-interaction-topics", label: "球迷话题", icon: "ri-chat-smile-3-line" },
      { id: "feedback-center", label: "用户反馈", icon: "ri-feedback-line" },
      { id: "abnormal-orders", label: "异常订单", icon: "ri-error-warning-line" },
      { id: "operation-logs", label: "操作日志", icon: "ri-history-line" },
      { id: "platform-settings", label: "平台设置", icon: "ri-settings-4-line" },
    ],
  },
];

export default function PlatformSidebar({
  activeSection,
  onNavigate,
  pendingCount,
  abnormalOrderCount = 0,
  onLogout,
}: Props) {
  const badgeMap: Record<string, number> = {
    "tenant-onboard": pendingCount,
    "abnormal-orders": abnormalOrderCount,
  };

  return (
    <aside className="w-60 flex-shrink-0 bg-gray-950 min-h-screen flex flex-col">
      {/* Platform Logo */}
      <div className="px-5 pt-5 pb-3 border-b border-gray-800">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 flex items-center justify-center bg-amber-500 rounded-xl flex-shrink-0">
            <i className="ri-apps-2-line text-white text-base"></i>
          </div>
          <div>
            <p className="text-white text-sm font-bold leading-tight">FanHub</p>
            <p className="text-gray-400 text-xs">平台运营端</p>
          </div>
        </div>

        {/* Platform Identity */}
        <div className="bg-gray-800/60 rounded-xl px-3 py-2.5 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-xs">当前身份</span>
            <span className="text-amber-400 text-xs font-medium flex items-center gap-1">
              <i className="ri-shield-star-line text-xs"></i> 平台超管
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-xs">在线租户</span>
            <span className="text-green-400 text-xs font-medium">4 / 6 运营中</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-xs">待处理</span>
            <span className="text-amber-400 text-xs font-medium">{pendingCount} 项</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {navGroups.map((group) => (
          <div key={group.title} className="mb-5">
            <p className="text-gray-600 text-xs font-semibold px-3 mb-1.5 uppercase tracking-wider">
              {group.title}
            </p>
            {group.items.map((item) => {
              const isActive = activeSection === item.id;
              const badge = badgeMap[item.id];
              return (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm cursor-pointer transition-all mb-0.5 ${
                    isActive
                      ? "bg-amber-500 text-white"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <i className={`${item.icon} text-base`}></i>
                    {item.label}
                  </span>
                  {badge && badge > 0 ? (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${isActive ? "bg-amber-400 text-white" : "bg-amber-500 text-white"}`}>
                      {badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="px-3 pb-4 border-t border-gray-800 pt-3">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-user-star-line text-white text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">平台管理员</p>
            <p className="text-gray-500 text-xs">ops@fanhub.com</p>
          </div>
        </div>
        <button
          onClick={onLogout}
          className="w-full mt-2 px-3 py-2 text-gray-500 hover:text-amber-400 text-xs flex items-center gap-2 cursor-pointer rounded-lg hover:bg-gray-800 transition-colors"
        >
          <i className="ri-logout-box-r-line"></i>
          退出平台端
        </button>
      </div>
    </aside>
  );
}
